// 51 个 SVG 图标组件（通过 createIcon helper 批量生成）
import * as React from 'react';

export interface IconProps {
  className?: string;
  size?: string | number;
  style?: React.CSSProperties;
}

interface IconOpts {
  strokeWidth?: number;
  fill?: string;
  size?: string | number;
  viewBox?: string;
}

type IconComponent = React.FC<IconProps>;

function createIcon(pathD: string, opts?: IconOpts): IconComponent {
  const o = opts || {};
  const strokeWidth = o.strokeWidth || 2;
  const fill = o.fill || 'none';
  return function IconComponent(props) {
    const p = props || {};
    const className = (p.className || '') + ' inline-block';
    const size = p.size || o.size || '1em';
    const style = Object.assign(
      { width: size, height: size, verticalAlign: '-0.125em' },
      p.style || {},
    );
    return React.createElement(
      'svg',
      {
        className: className,
        style: style,
        fill: fill,
        stroke: fill === 'none' ? 'currentColor' : undefined,
        viewBox: o.viewBox || '0 0 24 24',
        xmlns: 'http://www.w3.org/2000/svg',
      },
      React.createElement('path', {
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeWidth: fill === 'none' ? strokeWidth : undefined,
        d: pathD,
      }),
    );
  };
}

function createFilledIcon(pathD: string, opts?: IconOpts): IconComponent {
  return createIcon(pathD, Object.assign({ fill: 'currentColor' }, opts || {}));
}

const icons = {
  // Outline icons
  DashboardOutlined: createIcon(
    'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z',
    { viewBox: '0 0 24 24' },
  ),
  LaptopOutlined: createIcon(
    'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  ),
  KeyOutlined: createIcon(
    'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z',
  ),
  LineChartOutlined: createIcon(
    'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  ),
  AreaChartOutlined: createIcon('M3 3v18h18M7 14l3-4 4 3 5-7'),
  CodeOutlined: createIcon(
    'M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  ),
  CopyOutlined: createIcon(
    'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
  ),
  DownloadOutlined: createIcon('M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'),
  MessageOutlined: createIcon(
    'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  ),
  GlobalOutlined: createIcon(
    'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9',
  ),
  ApiOutlined: createIcon(
    'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01',
  ),
  ThunderboltOutlined: createIcon('M13 10V3L4 14h7v7l9-11h-7z'),
  ThunderboltFilled: createFilledIcon('M13 10V3L4 14h7v7l9-11h-7z'),
  ArrowUpOutlined: createIcon('M5 10l7-7m0 0l7 7m-7-7v18'),
  ArrowDownOutlined: createIcon('M19 14l-7 7m0 0l-7-7m7 7V3'),
  PlusOutlined: createIcon('M12 4v16m8-8H4'),
  ReloadOutlined: createIcon(
    'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  ),
  EditOutlined: createIcon(
    'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  ),
  DeleteOutlined: createIcon(
    'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  ),
  SendOutlined: createIcon('M12 19l9 2-9-18-9 18 9-2zm0 0v-8'),
  PaperClipOutlined: createIcon(
    'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13',
  ),
  InboxOutlined: createIcon(
    'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4',
  ),
  CloseCircleOutlined: createIcon(
    'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
  ),
  CheckOutlined: createIcon('M5 13l4 4L19 7'),
  LockOutlined: createIcon(
    'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  ),
  BookOutlined: createIcon(
    'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  ),
  MobileOutlined: createIcon(
    'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z',
  ),
  ProfileOutlined: createIcon(
    'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  ),
  MailOutlined: createIcon(
    'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  ),
  LockFilled: createFilledIcon(
    'M12 1a5 5 0 00-5 5v2H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V10a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 7H9V6a3 3 0 116 0v2z',
  ),
  InfoCircleFilled: createFilledIcon(
    'M12 2a10 10 0 100 20 10 10 0 000-20zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z',
  ),
  DashboardFilled: createFilledIcon(
    'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z',
  ),
  SearchOutlined: createIcon('M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'),
  SettingOutlined: createIcon(
    'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  ),
  ExclamationCircleOutlined: createIcon('M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'),
  EyeOutlined: createIcon(
    'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
  ),
  EyeInvisibleOutlined: createIcon(
    'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21',
  ),
  MenuOutlined: createIcon('M4 6h16M4 12h16M4 18h16'),
  MoreOutlined: createIcon(
    'M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z',
  ),
  MinusOutlined: createIcon('M20 12H4'),
  UpOutlined: createIcon('M5 15l7-7 7 7'),
  DownOutlined: createIcon('M19 9l-7 7-7-7'),
  LeftOutlined: createIcon('M15 19l-7-7 7-7'),
  RightOutlined: createIcon('M9 5l7 7-7 7'),
  QuestionCircleOutlined: createIcon(
    'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  ),
  WarningOutlined: createIcon(
    'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  ),
  UploadOutlined: createIcon('M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12'),
  FileOutlined: createIcon(
    'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  ),
  UserOutlined: createIcon('M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'),
  TeamOutlined: createIcon(
    'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  ),
  SwapOutlined: createIcon('M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4'),
  CloudServerOutlined: createIcon(
    'M3 15a4 4 0 004 4h9a5 5 0 10-.026-9.999A5.002 5.002 0 0012 4a5 5 0 00-4.938 4.177A4.001 4.001 0 003 15z',
  ),
  NotificationOutlined: createIcon(
    'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
  ),
  FilterOutlined: createIcon(
    'M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z',
  ),
  SortAscendingOutlined: createIcon('M3 4h13M3 8h9m-9 4h6m5 4v6m0 0l2-2m-2 2l-2-2'),
  SortDescendingOutlined: createIcon('M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l2-2m-2 2l-2-2'),
  HolderOutlined: createIcon(
    'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4',
  ),
};

// 命名导出每个 icon 便于 tree-shake（按需引入时 esbuild 会自动剔除未用图标）
// 以及一次性 export `icons` 对象（与旧 API 兼容）
export { icons };

// 把全部图标挂到 window.icons（旧代码 `const { icons } = window as any` 仍可用）
if (typeof window !== 'undefined') {
  (window as any).icons = { ...((window as any).icons || {}), ...icons };
}

// 单独命名导出，便于按需 import
export const {
  DashboardOutlined,
  LaptopOutlined,
  KeyOutlined,
  LineChartOutlined,
  AreaChartOutlined,
  CodeOutlined,
  CopyOutlined,
  DownloadOutlined,
  MessageOutlined,
  GlobalOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  ThunderboltFilled,
  ArrowUpOutlined,
  ArrowDownOutlined,
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  PaperClipOutlined,
  InboxOutlined,
  CloseCircleOutlined,
  CheckOutlined,
  LockOutlined,
  BookOutlined,
  MobileOutlined,
  ProfileOutlined,
  MailOutlined,
  LockFilled,
  InfoCircleFilled,
  DashboardFilled,
  SearchOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  MenuOutlined,
  MoreOutlined,
  MinusOutlined,
  UpOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
  UploadOutlined,
  FileOutlined,
  UserOutlined,
  TeamOutlined,
  SwapOutlined,
  CloudServerOutlined,
  NotificationOutlined,
  FilterOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  HolderOutlined,
} = icons;
